/**
 * One review submission grouped by reviewer + submission time.
 * Collapsible header with avatar, name, relative time, comment count, and a kebab menu.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, MoreHorizontal, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import type { OwnerReview, ReviewCommentStatus } from './types';
import { ReviewerAvatar } from './ReviewerAvatar';
import { ReviewCommentRow } from './ReviewCommentRow';
import { relativeTime } from './format';

interface Props {
  review: OwnerReview;
  /** Set of comment IDs currently in the chat queue. */
  queuedCommentIds: Set<string>;
  defaultOpen?: boolean;
  onSeek: (timestamp: number, slideId: string) => void;
  onAddToChat: (review: OwnerReview, commentId: string) => void;
  onRemoveFromChat: (commentId: string) => void;
  onSetCommentStatus: (review: OwnerReview, commentId: string, status: ReviewCommentStatus) => void;
  onDeleteReview: (reviewId: string) => void;
}

export function ReviewCard({
  review,
  queuedCommentIds,
  defaultOpen = true,
  onSeek,
  onAddToChat,
  onRemoveFromChat,
  onSetCommentStatus,
  onDeleteReview,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [menuOpen, setMenuOpen] = useState(false);

  const total = review.comments.length;
  const sentCount = review.comments.filter((c) => c.status === 'sent').length;
  const queuedCount = review.comments.filter((c) => c.status === 'accepted').length;
  const openCount = review.comments.filter((c) => c.status === 'pending').length;

  return (
    <div className="rounded-lg border border-black/5 dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.02] overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 py-2">
        <ReviewerAvatar name={review.reviewerName} />
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className="text-[13px] font-semibold studio-text-primary truncate">{review.reviewerName}</span>
              <span className="text-[11px] studio-text-secondary flex-shrink-0">{relativeTime(review.submittedAt)}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11px] studio-text-secondary">
                {total} comment{total === 1 ? '' : 's'}
              </span>
              {openCount > 0 && (
                <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-warning/20 text-foreground">
                  {openCount} open
                </span>
              )}
              {queuedCount > 0 && (
                <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-accent text-accent-foreground">
                  {queuedCount} queued
                </span>
              )}
              {sentCount > 0 && (
                <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-success/15 text-success">
                  {sentCount} sent
                </span>
              )}
            </div>
          </div>
          <ChevronDown
            className={cn('h-4 w-4 studio-text-secondary transition-transform flex-shrink-0', open && 'rotate-180')}
          />
        </button>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-black/[0.06] dark:hover:bg-white/[0.08] studio-text-secondary"
              aria-label="Review actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onDeleteReview(review.id)} className="text-[12px] text-destructive">
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              Delete review
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Comments */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="border-t border-black/5 dark:border-white/[0.05] overflow-hidden"
          >
            <div className="py-1">
              {review.comments.map((comment) => (
                <ReviewCommentRow
                  key={comment.id}
                  review={review}
                  comment={comment}
                  isQueued={queuedCommentIds.has(comment.id)}
                  onSeek={onSeek}
                  onAddToChat={() => onAddToChat(review, comment.id)}
                  onRemoveFromChat={() => onRemoveFromChat(comment.id)}
                  onSetStatus={(status) => onSetCommentStatus(review, comment.id, status)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
